import { createContext, useContext, useMemo, useReducer } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

export const GraphContext = createContext()

const PREFERENCE_SUBTYPE_BASE = "graph"

function getPreferenceSubtype(panelId) {
  return panelId ? `${PREFERENCE_SUBTYPE_BASE}-${panelId}` : PREFERENCE_SUBTYPE_BASE
}

const DEFAULT_STATE = {
  selectedTemplate: "performance",
  disabledItems: { residual: true },
  dateOverride: null,
}

const PREFERENCE_FIELDS = [
  "selectedTemplate",
  "disabledItems",
]

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedTemplate":
        draft.selectedTemplate = action.template
        break
      case "setDisabledItems": {
        const currentState = draft.disabledItems[action.itemId]
        draft.disabledItems[action.itemId] = currentState === undefined ? !action.defaultDisabled : !currentState
        break
      }
      case "setOnlyItem": {
        const allItemIds = action.allItemIds || []
        const targetItemId = action.itemId

        Object.assign(
          draft.disabledItems,
          Object.fromEntries(allItemIds.map(itemId => [itemId, itemId !== targetItemId])),
        )
        break
      }
      case "enableItems": {
        const itemIds = action.itemIds || []

        Object.assign(
          draft.disabledItems,
          Object.fromEntries(itemIds.map(itemId => [itemId, false])),
        )
        break
      }
      case "setDateOverride":
        draft.dateOverride = action.dateOverride
        break
      case "clearDateOverride":
        draft.dateOverride = null
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences) {
  return { type: "setPreferences", preferences }
}

export default function useGraphContext(parentContext, config, panelId) {
  const parentContextValue = useContext(parentContext)
  const initialTemplate = config?.selectedTemplate ?? DEFAULT_STATE.selectedTemplate

  const [state, dispatch] = useReducer(reducer, {
    ...DEFAULT_STATE,
    selectedTemplate: initialTemplate,
  })

  const isPreferencesLoaded = usePreferences(
    getPreferenceSubtype(panelId),
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setSelectedTemplate: (template) => dispatch({ type: "setSelectedTemplate", template }),
    setDisabledItems: (itemId, defaultDisabled) => dispatch({ type: "setDisabledItems", itemId, defaultDisabled }),
    setOnlyItem: (itemId, allItemIds) => dispatch({ type: "setOnlyItem", itemId, allItemIds }),
    enableItems: (itemIds) => dispatch({ type: "enableItems", itemIds }),
    setDateOverride: (dateOverride) => dispatch({ type: "setDateOverride", dateOverride }),
    clearDateOverride: () => dispatch({ type: "clearDateOverride" }),
  }), [dispatch])

  const effectiveDates = useMemo(() => ({
    startDate: state.dateOverride?.startDate ?? parentContextValue.startDate,
    endDate: state.dateOverride?.endDate ?? parentContextValue.endDate,
  }), [state.dateOverride, parentContextValue.startDate, parentContextValue.endDate])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    ...effectiveDates,
    isPreferencesLoaded,
  }), [parentContextValue, state, contextUpdaters, effectiveDates, isPreferencesLoaded])

  return [GraphContext, contextValue]
}
